/**
 * Contact Form View
 * Allows users to send messages to the site owner via email
 */

import React, {useState, useEffect} from "react";
import {Button, Card, CardBody, CardFooter, Row, Col, Alert} from "reactstrap";
import {Form, FormGroup, Input, Label} from "reactstrap";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";
import {useForm, Controller} from "react-hook-form";
import {useUser} from "../contexts/AuthContext";
import PageHeader from "../components/Common/PageHeader";
import toast from "react-hot-toast";
import {submitContactForm} from "../services/contactService";
import {ContactFormData, ContactErrorResponse} from "../types/contactFormTypes";

interface ContactFormInputs {
  name: string;
  email: string;
  subject: string;
  message: string;
}

function Contact() {
  const {user} = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: {errors}
  } = useForm<ContactFormInputs>({
    defaultValues: {
      name: user.authenticated ? `${user.settings.firstName} ${user.settings.lastName}`.trim() : "",
      email: user.authenticated ? user.email : "",
      subject: "",
      message: ""
    }
  });

  // Update form when user authentication state changes
  useEffect(() => {
    if (user.authenticated) {
      reset({
        name: `${user.settings.firstName} ${user.settings.lastName}`.trim(),
        email: user.email,
        subject: "",
        message: ""
      });
    }
  }, [user.authenticated, user.email, user.settings.firstName, user.settings.lastName, reset]);

  async function onSubmit(formData: ContactFormInputs) {
    try {
      setIsSubmitting(true);
      setSubmitSuccess(false);

      // Prepare contact form data
      const contactData: ContactFormData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        timestamp: new Date().toISOString()
      };

      // Include user ID if authenticated
      if (user.authenticated && user.identityId) {
        contactData.userId = user.identityId;
      }

      // Submit form
      await submitContactForm(contactData, user.authenticated ? user.jwtToken : undefined);

      // Success
      toast.success("Message sent successfully! We'll get back to you soon.");
      setSubmitSuccess(true);

      // Clear form
      reset({
        name: user.authenticated ? `${user.settings.firstName} ${user.settings.lastName}`.trim() : "",
        email: user.authenticated ? user.email : "",
        subject: "",
        message: ""
      });
    } catch (error: any) {
      const err = error as ContactErrorResponse;

      // Handle different error types
      if (err.error?.code === "RATE_LIMIT_EXCEEDED") {
        const remainingMinutes = err.error.details?.remainingMinutes || 60;
        toast.error(
          `Too many submissions. Please try again in ${remainingMinutes} minute${remainingMinutes === 1 ? "" : "s"}.`
        );
      } else if (err.error?.code === "VALIDATION_ERROR") {
        toast.error(`Invalid form data: ${err.error.message}`);
      } else if (err.error?.code === "NETWORK_ERROR") {
        toast.error("Network error. Please check your connection and try again.");
      } else {
        toast.error(err.error?.message || "Failed to send message. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <>
          <PageHeader title="Contact Us" subtitle="Send us a message and we'll get back to you" icon="fa fa-envelope" />
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col lg="8" md="12">
                <Card>
                  <CardBody>
                    {submitSuccess && (
                      <Alert color="success" className="mb-3">
                        <i className="fa fa-check-circle mr-2"></i>
                        Your message has been sent successfully! We'll respond to your email address shortly.
                      </Alert>
                    )}

                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label>
                            Name <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            name="name"
                            control={control}
                            rules={{
                              required: "Name is required",
                              minLength: {value: 1, message: "Name is required"},
                              maxLength: {value: 100, message: "Name must be 100 characters or less"}
                            }}
                            render={({field}) => (
                              <Input
                                type="text"
                                {...field}
                                invalid={!!errors.name}
                                disabled={isSubmitting}
                                placeholder="Your full name"
                              />
                            )}
                          />
                          {errors.name && <small className="text-danger">{errors.name.message}</small>}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>
                            Email <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            name="email"
                            control={control}
                            rules={{
                              required: "Email is required",
                              pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: "Invalid email address"
                              }
                            }}
                            render={({field}) => (
                              <Input
                                type="email"
                                {...field}
                                invalid={!!errors.email}
                                disabled={isSubmitting}
                                placeholder="your@email.com"
                              />
                            )}
                          />
                          {errors.email && <small className="text-danger">{errors.email.message}</small>}
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup>
                      <Label>
                        Subject <span className="text-danger">*</span>
                      </Label>
                      <Controller
                        name="subject"
                        control={control}
                        rules={{
                          required: "Subject is required",
                          minLength: {value: 1, message: "Subject is required"},
                          maxLength: {value: 200, message: "Subject must be 200 characters or less"}
                        }}
                        render={({field}) => (
                          <Input
                            type="text"
                            {...field}
                            invalid={!!errors.subject}
                            disabled={isSubmitting}
                            placeholder="Brief description of your inquiry"
                          />
                        )}
                      />
                      {errors.subject && <small className="text-danger">{errors.subject.message}</small>}
                    </FormGroup>

                    <FormGroup>
                      <Label>
                        Message <span className="text-danger">*</span>
                      </Label>
                      <Controller
                        name="message"
                        control={control}
                        rules={{
                          required: "Message is required",
                          minLength: {value: 10, message: "Message must be at least 10 characters"},
                          maxLength: {value: 5000, message: "Message must be 5000 characters or less"}
                        }}
                        render={({field}) => (
                          <Input
                            type="textarea"
                            rows="20"
                            {...field}
                            invalid={!!errors.message}
                            disabled={isSubmitting}
                            placeholder="Tell us how we can help you..."
                          />
                        )}
                      />
                      {errors.message && <small className="text-danger">{errors.message.message}</small>}
                      <small className="text-muted d-block mt-1">
                        {control._formValues.message?.length || 0} / 5000 characters
                      </small>
                    </FormGroup>
                  </CardBody>
                  <CardFooter>
                    <Button color="primary" data-color={color} type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <i className="fa fa-spinner fa-spin mr-1"></i>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-paper-plane mr-1"></i>
                          Send Message
                        </>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </Col>

              <Col lg="4" md="12">
                <Card>
                  <CardBody>
                    <h5 className="card-title">
                      <i className="fa fa-info-circle mr-2"></i>
                      Contact Information
                    </h5>
                    <p className="text-muted">
                      Fill out the form and we'll get back to you via email as soon as possible.
                    </p>
                    <hr />
                    <h6 className="font-weight-bold">Response Time</h6>
                    <p className="text-muted">We typically respond within 24-48 hours during business days.</p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
}

export default Contact;
